import {
  MoreVertical,
  RotateCcw,
  X,
} from 'lucide-react'

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import {
  useContextualSettings,
} from './ContextualSettingsContext'

interface Props {
  id: string
  title: string

  allowHide?: boolean
  showBaseControls?: boolean

  children?: ReactNode
}

export function ContextualMenu({
  id,
  title,

  allowHide = true,
  showBaseControls = true,

  children,
}: Props) {
  const [
    open,
    setOpen,
  ] =
    useState(false)

  const ref =
    useRef<HTMLDivElement>(
      null,
    )

  const {
    get,
    patch,
    reset,
  } =
    useContextualSettings()

  const settings =
    get(id)

  useEffect(() => {
    if (!open) {
      return
    }

    const close = (
      event: MouseEvent,
    ) => {
      if (
        ref.current &&
        !ref.current.contains(
          event.target as Node,
        )
      ) {
        setOpen(false)
      }
    }

    window.addEventListener(
      'mousedown',
      close,
    )

    return () =>
      window.removeEventListener(
        'mousedown',
        close,
      )
  }, [open])

  return (
    <div
      ref={ref}
      className="r7-context-root"
    >
      <button
        type="button"
        className="r7-context-trigger"
        title={`Personalizar ${title}`}
        aria-label={`Personalizar ${title}`}
        onClick={() =>
          setOpen(
            current => !current,
          )
        }
      >
        <MoreVertical
          size={17}
        />
      </button>

      {open && (
        <div className="r7-context-popover">

          <header>
            <div>
              <strong>
                {title}
              </strong>

              <small>
                Configuración local
              </small>
            </div>

            <button
              type="button"
              className="r7-icon-button"
              onClick={() =>
                setOpen(false)
              }
            >
              <X size={15} />
            </button>
          </header>

          {showBaseControls && (
            <>
              <label className="r7-range">
                <span>
                  Tamaño del texto

                  <strong>
                    {Math.round(
                      settings.textScale *
                        100,
                    )}%
                  </strong>
                </span>

                <input
                  type="range"
                  min=".70"
                  max="1.60"
                  step=".02"
                  value={
                    settings.textScale
                  }
                  onChange={
                    event =>
                      patch(
                        id,
                        {
                          textScale:
                            Number(
                              event.target.value,
                            ),
                        },
                      )
                  }
                />
              </label>

              <div className="r7-color-pair">

                <label>
                  <span>
                    Texto
                  </span>

                  <input
                    type="color"
                    value={
                      settings.textColor ??
                      '#e4edf5'
                    }
                    onChange={
                      event =>
                        patch(
                          id,
                          {
                            textColor:
                              event.target.value,
                          },
                        )
                    }
                  />
                </label>

                <label>
                  <span>
                    Fondo
                  </span>

                  <input
                    type="color"
                    value={
                      settings.backgroundColor ??
                      '#071622'
                    }
                    onChange={
                      event =>
                        patch(
                          id,
                          {
                            backgroundColor:
                              event.target.value,
                          },
                        )
                    }
                  />
                </label>

              </div>
            </>
          )}

          {children}

          {allowHide && (
            <label className="r7-toggle">
              <input
                type="checkbox"
                checked={
                  settings.visible
                }
                onChange={
                  event =>
                    patch(
                      id,
                      {
                        visible:
                          event.target.checked,
                      },
                    )
                }
              />

              Mostrar módulo
            </label>
          )}

          <button
            type="button"
            className="r7-reset"
            onClick={() => {
              reset(id)
              setOpen(false)
            }}
          >
            <RotateCcw
              size={14}
            />

            Restaurar
          </button>

        </div>
      )}
    </div>
  )
}
